import React, { useState, useEffect, useMemo } from "react";
import {
  format,
  addHours,
  addSeconds,
  isAfter,
  parseISO,
  differenceInSeconds,
} from "date-fns";
import { toZonedTime } from "date-fns-tz";
import { ArrowUpDown } from "lucide-react";
import { useUser } from "../contexts/UserContext";

const Toast = ({ message, type, onClose }) => {
  useEffect(() => {
    const timer = setTimeout(() => {
      onClose();
    }, 3000);

    return () => clearTimeout(timer);
  }, [onClose]);

  return (
    <div
      className={`fixed top-4 right-4 px-4 py-2 rounded-lg shadow-lg transition-opacity duration-300 z-50
        ${type === "success" ? "bg-green-500" : "bg-red-500"} 
        text-white text-sm font-medium`}
    >
      {message}
    </div>
  );
};

const ClaimTable = () => {
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState("");
  const [toastType, setToastType] = useState("success");
  const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
  const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const [apps, setApps] = useState([]);
  const [timers, setTimers] = useState({});
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [sortConfig, setSortConfig] = useState({
    key: "status",
    direction: "asc",
  });
  const itemsPerPage = 50;
  const { user } = useUser(); // Add user context

  useEffect(() => {
    const interval = setInterval(() => {
      setTimers((prevTimers) => {
        const updatedTimers = { ...prevTimers };
        for (const key in updatedTimers) {
          if (updatedTimers[key] > 0) {
            updatedTimers[key] -= 1;
          }
        }
        return updatedTimers;
      });
    }, 1000);

    return () => clearInterval(interval);
  }, []);

  function convertTimeStringToLocalTime(app) {
    // 1. Split the time string into hours and minutes
    const timeString = app.fixedTime;
    const lastClaimTimeUTC = app.lastClaim;
    const [hours, minutes] = timeString.split(":").map(Number);

    const lastClaimLocalDate = new Date(lastClaimTimeUTC);

    // 2. Get the current date and time
    const now = new Date();

    // 3. Create target time based on last claim date
    const targetTime = new Date(lastClaimLocalDate);
    targetTime.setHours(hours);
    targetTime.setMinutes(minutes);
    targetTime.setSeconds(0);
    targetTime.setMilliseconds(0);

    // 4. If the target time is earlier than current time,
    // add one day to the target time
    if (targetTime < now && lastClaimLocalDate > targetTime) {
      targetTime.setDate(targetTime.getDate() + 1);
    }

    return targetTime;
  }

  const updateTimers = (apps) => {
    const newTimers = {};
    apps.forEach((app) => {
      if (app.alertType == "scheduled") {
        const nextClaim = convertTimeStringToLocalTime(app);
        const remainingTime = nextClaim
          ? Math.max(0, differenceInSeconds(nextClaim, new Date()))
          : 0;
        newTimers[app._id] = remainingTime;
      } else {
        const lastClaim = app.lastClaim ? parseISO(app.lastClaim) : null;
        const nextClaim = lastClaim ? addHours(lastClaim, app.duration) : null;
        const remainingTime = nextClaim
          ? Math.max(0, differenceInSeconds(nextClaim, new Date()))
          : 0;
        newTimers[app._id] = remainingTime;
      }
    });
    setTimers(newTimers);
  };

  const fetchApps = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}apps/${user.id}`);
      if (response.ok) {
        const data = await response.json();
        setApps(data);
        updateTimers(data);
      } else {
        console.error("Failed to fetch apps");
      }
    } catch (error) {
      console.error("Error fetching apps:", error);
    }
  };

  useEffect(() => {
    fetchApps();
  }, []);

  const handleClaim = async (app) => {
    const currentDateTime = new Date().toISOString();

    try {
      const response = await fetch(`${API_BASE_URL}apps/${app._id}`, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          lastClaim: currentDateTime,
        }),
      });

      if (response.ok) {
        setToastMessage(`Claimed ${app.name} Successfully!`);
        setToastType("success");
        setShowToast(true);
        fetchApps();
      } else {
        throw new Error("Failed to claim");
      }
    } catch (error) {
      console.error("Error updating claim:", error);
      setToastMessage(`Error claiming ${app.name}. Please try again.`);
      setToastType("error");
      setShowToast(true);
    }
  };

  const handleCloseToast = () => {
    setShowToast(false);
  };

  const handleSort = (key) => {
    let direction = "asc";
    if (sortConfig.key === key && sortConfig.direction === "asc") {
      direction = "desc";
    }
    setSortConfig({ key, direction });
  };

  const getSortedData = (data) => {
    if (!sortConfig.key) return data;

    return [...data].sort((a, b) => {
      let aValue = a[sortConfig.key];
      let bValue = b[sortConfig.key];

      // Special handling for different columns
      if (sortConfig.key === "status") {
        const aTimer = timers[a._id] || 0;
        const bTimer = timers[b._id] || 0;
        aValue = aTimer;
        bValue = bTimer;
      }

      if (aValue === null) return 1;
      if (bValue === null) return -1;

      if (typeof aValue === "string") {
        aValue = aValue.toLowerCase();
        bValue = bValue.toLowerCase();
      }

      if (aValue < bValue) return sortConfig.direction === "asc" ? -1 : 1;
      if (aValue > bValue) return sortConfig.direction === "asc" ? 1 : -1;
      return 0;
    });
  };

  const filteredApps = useMemo(() => {
    return apps.filter(
      (app) =>
        app.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        app.website.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [apps, searchTerm]);

  const sortedAndFilteredApps = useMemo(() => {
    return getSortedData(filteredApps);
  }, [filteredApps, sortConfig]);

  const paginatedApps = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    return sortedAndFilteredApps.slice(startIndex, startIndex + itemsPerPage);
  }, [sortedAndFilteredApps, currentPage]);

  const totalPages = Math.ceil(filteredApps.length / itemsPerPage);

  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
  };

  const handleOpenReadyWebsites = () => {
    apps.forEach((app) => {
      const remainingTime = timers[app._id] || 0;
      if (remainingTime === 0 && app.website) {
        const normalizedUrl =
          app.website.startsWith("http://") ||
          app.website.startsWith("https://")
            ? app.website
            : `https://${app.website}`;
        window.open(normalizedUrl, "_blank");
      }
    });
  };

  const formatTime = (seconds) => {
    const hrs = Math.floor(seconds / 3600);
    const mins = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    return `${hrs.toString().padStart(2, "0")}:${mins
      .toString()
      .padStart(2, "0")}:${secs.toString().padStart(2, "0")}`;
  };

  const getNormalizedUrl = (website) => {
    return website.startsWith("http://") || website.startsWith("https://")
      ? website
      : `https://${website}`;
  };

  const renderSortIcon = (columnKey) => {
    return (
      <ArrowUpDown
        className={`inline ml-2 h-4 w-4 ${
          sortConfig.key === columnKey ? "opacity-100" : "opacity-50"
        }`}
      />
    );
  };

  return (
    <div className="bg-white shadow-md rounded-lg p-2 sm:p-4">
      {showToast && (
        <Toast
          message={toastMessage}
          type={toastType}
          onClose={handleCloseToast}
        />
      )}
      <div className="mb-4 flex flex-col sm:flex-row items-center gap-2">
        <input
          type="text"
          value={searchTerm}
          onChange={(e) => {
            setSearchTerm(e.target.value);
            setCurrentPage(1);
          }}
          placeholder="Search by name"
          className="w-full sm:flex-grow p-2 border rounded"
        />
        <button
          onClick={handleOpenReadyWebsites}
          className="w-full sm:w-auto px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
        >
          Open All Ready
        </button>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="bg-gray-100">
              <th
                className="p-2 text-left cursor-pointer hover:bg-gray-200"
                onClick={() => handleSort("name")}
              >
                Name {renderSortIcon("name")}
              </th>
              <th
                className="hidden sm:table-cell p-2 text-left cursor-pointer hover:bg-gray-200"
                onClick={() => handleSort("duration")}
              >
                Duration {renderSortIcon("duration")}
              </th>
              <th
                className="p-2 text-left cursor-pointer hover:bg-gray-200"
                onClick={() => handleSort("status")}
              >
                Status {renderSortIcon("status")}
              </th>
              <th className="p-2 text-center">Action</th>
            </tr>
          </thead>
          <tbody>
            {paginatedApps.map((app) => {
              const currentDateTime = new Date();
              const lastClaimTimeUTC = app.lastClaim
                ? parseISO(app.lastClaim)
                : null;
              const lastClaimLocalDate = new Date(lastClaimTimeUTC);
              const nextClaim =
                app.alertType == "scheduled"
                  ? convertTimeStringToLocalTime(app)
                  : lastClaimLocalDate
                  ? addHours(lastClaimLocalDate, app.duration)
                  : null;

              const remainingTime = timers[app._id] || 0;
              const isClaimable =
                !lastClaimTimeUTC || isAfter(currentDateTime, nextClaim);

              const completionTime =
                remainingTime > 0
                  ? toZonedTime(addSeconds(new Date(), remainingTime), timeZone)
                  : null;

              return (
                <tr
                  key={app._id}
                  className={`border-b ${isClaimable ? "bg-green-50" : ""}`}
                >
                  <td className="p-2 max-w-[120px] sm:max-w-none">
                    <div className="truncate">
                      {app.website ? (
                        <a
                          href={getNormalizedUrl(app.website)}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-blue-600 hover:text-blue-800 hover:underline"
                        >
                          {app.name}
                        </a>
                      ) : (
                        app.name
                      )}
                    </div>
                  </td>
                  <td className="hidden sm:table-cell p-2">{app.duration}</td>
                  <td className="p-2">
                    {isClaimable ? (
                      <span className="px-2 py-1 rounded text-xs bg-green-200 text-green-800">
                        Ready
                      </span>
                    ) : (
                      <span
                        className="px-2 py-1 rounded text-xs bg-gray-200 text-gray-800 relative group cursor-help"
                        title={
                          completionTime
                            ? format(completionTime, "MMM d, yyyy hh:mm:ss a")
                            : ""
                        }
                      >
                        {formatTime(remainingTime)}
                        <div className="invisible group-hover:visible absolute z-10 px-4 py-3 text-sm font-medium text-white bg-gray-900 rounded-lg shadow-sm dark:bg-gray-700 -top-14 left-1/2 transform -translate-x-1/2 whitespace-nowrap">
                          {completionTime
                            ? format(completionTime, "MMM d, yyyy hh:mm:ss a")
                            : ""}
                        </div>
                      </span>
                    )}
                  </td>
                  <td className="p-2 text-center">
                    <button
                      onClick={() => handleClaim(app)}
                      disabled={!true}
                      className={`
                        w-full sm:w-auto px-4 py-2 rounded text-sm
                        ${
                          true
                            ? "bg-blue-500 text-white hover:bg-blue-600"
                            : "bg-gray-300 text-gray-500 cursor-not-allowed"
                        }
                      `}
                    >
                      Claim
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <div className="mt-4 flex flex-col sm:flex-row justify-between items-center gap-2">
        <p className="text-sm">Total Claims: {filteredApps.length}</p>
        <div className="flex items-center space-x-2">
          <button
            onClick={() => handlePageChange(currentPage - 1)}
            disabled={currentPage === 1}
            className="px-3 py-1 text-sm bg-gray-200 rounded disabled:opacity-50"
          >
            Previous
          </button>
          <span className="text-sm">
            Page {currentPage} of {totalPages}
          </span>
          <button
            onClick={() => handlePageChange(currentPage + 1)}
            disabled={currentPage === totalPages}
            className="px-3 py-1 text-sm bg-gray-200 rounded disabled:opacity-50"
          >
            Next
          </button>
        </div>
      </div>
    </div>
  );
};

export default ClaimTable;
